import { useState } from "react";
import { ChevronDown, Lightbulb } from "lucide-react";

/**
 * InsightCard — expandable inline IAL explanation.
 *
 * Props:
 *   assist: array of { signal, value, cause, effect, implication }
 *   compact: boolean — if true, renders a minimal trigger (for table rows)
 *
 * Default: collapsed. On tap: expand inline. No modal, no popup.
 */
const InsightCard = ({ assist, compact = false }) => {
  const [expanded, setExpanded] = useState(false);

  if (!assist || assist.length === 0) return null;

  if (compact) {
    return (
      <div className="relative inline-block">
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider transition-all duration-200 ${
            expanded
              ? "bg-[#F97316]/20 text-[#F97316] border border-[#F97316]/30"
              : "bg-white/5 text-white/30 hover:text-[#F97316] hover:bg-[#F97316]/10 border border-transparent"
          }`}
          data-testid="insight-trigger"
        >
          <Lightbulb className="w-2.5 h-2.5" />
          Why
          <ChevronDown className={`w-2.5 h-2.5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
        </button>

        {expanded && (
          <div className="absolute z-50 left-0 top-full mt-1 w-[320px] bg-[#0d0f12] border border-[#F97316]/20 rounded-lg shadow-xl shadow-black/50 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200" data-testid="insight-expanded">
            {assist.map((a, i) => (
              <ExplanationRow key={i} explanation={a} isLast={i === assist.length - 1} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Full-width card mode (for intelligence cards, post-race review)
  return (
    <div className="mt-2" data-testid="insight-card">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider transition-all duration-200 ${
          expanded ? "text-[#F97316]" : "text-white/25 hover:text-[#F97316]"
        }`}
        data-testid="insight-trigger"
      >
        <Lightbulb className="w-3 h-3" />
        {expanded ? "Intelligence Assist" : "Why?"}
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="mt-2 rounded-lg border border-[#F97316]/15 bg-[#F97316]/[0.03] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200" data-testid="insight-expanded">
          {assist.map((a, i) => (
            <ExplanationRow key={i} explanation={a} isLast={i === assist.length - 1} />
          ))}
        </div>
      )}
    </div>
  );
};

const ExplanationRow = ({ explanation, isLast }) => {
  const { signal, value, cause, effect, implication } = explanation;
  const signalColour = {
    efficiency: "#10B981",
    pressure: "#EF4444",
    momentum: "#3B82F6",
    bias: "#A855F7",
    path_cost: "#F59E0B",
    trend: "#06B6D4",
    scenario_match: "#F97316",
    consistency: "#8B5CF6",
    pressure_tolerance: "#EC4899",
    next_start: "#F97316",
    late_strength: "#14B8A6",
  }[signal] || "#F97316";

  return (
    <div className={`px-3 py-2.5 ${!isLast ? "border-b border-white/5" : ""}`}>
      {/* Signal badge */}
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider"
          style={{ backgroundColor: `${signalColour}15`, color: signalColour }}
        >
          {signal.replace("_", " ")}
        </span>
        <span className="text-white/50 text-[10px] font-mono">{value}</span>
      </div>
      {/* Explanations */}
      <div className="space-y-1">
        <ExplanationLine label="Cause" text={cause} />
        <ExplanationLine label="Effect" text={effect} />
        <ExplanationLine label="Implication" text={implication} />
      </div>
    </div>
  );
};

const ExplanationLine = ({ label, text }) => (
  <div className="flex items-start gap-2 text-[11px]">
    <span className="text-white/25 uppercase text-[9px] font-bold tracking-wider w-16 flex-shrink-0 pt-0.5">{label}</span>
    <span className="text-white/60 leading-relaxed">{text}</span>
  </div>
);

// Pre-expanded version for Guided Tour / showcase
export const InsightCardExpanded = ({ assist }) => {
  if (!assist || assist.length === 0) return null;
  return (
    <div className="rounded-lg border border-[#F97316]/20 bg-[#F97316]/[0.03] overflow-hidden" data-testid="insight-expanded-showcase">
      <div className="px-3 py-2 border-b border-[#F97316]/10 flex items-center gap-1.5">
        <Lightbulb className="w-3.5 h-3.5 text-[#F97316]" />
        <span className="text-[#F97316] text-[10px] font-bold uppercase tracking-widest">Intelligence Assist</span>
      </div>
      {assist.map((a, i) => (
        <ExplanationRow key={i} explanation={a} isLast={i === assist.length - 1} />
      ))}
    </div>
  );
};

export default InsightCard;
